import { useContext, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';

import { NativeDataContext } from 'bundles/ent-website/components/NativeDataContext';
import FindByPromoCodeId from 'bundles/ent-website/utils/FindByPromoCodeId.graphql';
import type {
  FindByEnterprisePromotionIdQuery as FindByEnterprisePromotionIdQueryType,
  FindByEnterprisePromotionIdQueryVariables,
} from 'bundles/ent-website/utils/__generated__/FindByEnterprisePromotionIdQuery';
import type {
  FindByPromoCodeIdQuery,
  FindByPromoCodeIdQueryVariables,
} from 'bundles/ent-website/utils/__generated__/FindByPromoCodeId';
import type {
  FindByPromoCodeQuery as FindByPromoCodeQueryType,
  FindByPromoCodeQueryVariables,
} from 'bundles/ent-website/utils/__generated__/FindByPromoCodeQuery';
import {
  findByEnterprisePromotionIdQuery,
  findByPromoCodeQuery as findByPromoCodeQueryLegacy,
} from 'bundles/ent-website/utils/withEnterprisePromotionQuery';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';

function PromoDataSource() {
  const { query } = useLocation();
  const promoCode = query?.promoCode;

  const useJavaForFindByPromoCode = EnterpriseExperiments.get('useJavaForFindByPromoCode');

  // This query will only run when promo code is present in the URL & the experiment useJavaForFindByPromoCode is enabled
  const { data: findByPromoCodeIdData } = useQuery<FindByPromoCodeIdQuery, FindByPromoCodeIdQueryVariables>(
    FindByPromoCodeId,
    {
      skip: !promoCode || !useJavaForFindByPromoCode,
      variables: { promoCodeId: simplifyQueryValue(promoCode)! },
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );

  // This query will only run when promo code is present in the URL & the experiment useJavaForFindByPromoCode is disabled
  // TODO: Remove this query when the experiment is live on 100% of users
  const { data: findByPromoCodeLegacyData } = useQuery<FindByPromoCodeQueryType, FindByPromoCodeQueryVariables>(
    findByPromoCodeQueryLegacy,
    {
      skip: !promoCode || useJavaForFindByPromoCode,
      variables: { promoCodeId: simplifyQueryValue(promoCode)! },
      errorPolicy: 'all',
    }
  );

  const enterprisePromotion = useMemo(() => {
    if (useJavaForFindByPromoCode) {
      if (!findByPromoCodeIdData) return {};

      return {
        id: findByPromoCodeIdData.EnterprisePromotionQueries?.getByPromoCodeId.enterprisePromotion?.id,
        promotionId:
          findByPromoCodeIdData.EnterprisePromotionQueries?.getByPromoCodeId.enterprisePromotion?.promotion.id,
      };
    } else {
      if (!findByPromoCodeLegacyData) return {};
      return {
        id: findByPromoCodeLegacyData?.EnterprisePromotionsV1Resource.findByPromoCode?.elements[0]?.id,
        promotionId:
          findByPromoCodeLegacyData?.EnterprisePromotionsV1Resource.findByPromoCode?.elements[0]?.promotionId,
      };
    }
  }, [findByPromoCodeIdData, findByPromoCodeLegacyData, useJavaForFindByPromoCode]);

  const { data: findByPromoIdData } = useQuery<
    FindByEnterprisePromotionIdQueryType,
    FindByEnterprisePromotionIdQueryVariables
  >(findByEnterprisePromotionIdQuery, {
    skip: !enterprisePromotion?.id,
    variables: { id: enterprisePromotion.id ?? '' },
    errorPolicy: 'all',
  });

  const promotion = findByPromoIdData?.PromotionsV3Resource?.byEnterprisePromotionId?.elements?.[0];

  const { setNativeData } = useContext(NativeDataContext);

  useEffect(() => {
    setNativeData('promo', {
      promoCode,
      percent: promotion?.discountPercent,
      name: promotion?.promotionName,
      promotion,
      enterprisePromotion,
    });
  }, [setNativeData, promoCode, promotion, enterprisePromotion]);

  return null;
}

export default PromoDataSource;
